import React, { type HTMLAttributes } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';

import { Container, WysiwygField } from '@uncinc/uncinc-react-kitchen-sink';

import { ButtonWrapper, ParagraphsRenderer, VariableRatiosPicture } from '../../../components';
import useLayoutVariables from '../../../hooks/useLayoutVariables';
import type { NodeHomePage as Type } from '../../../types/uncinc_profile/nodes';

import './style.scss';

interface HomePageProps extends React.HTMLProps<HTMLAttributes<HTMLDivElement>> {
  entity: ImmutableEntity<Type>;
}

const NodeHomePage: React.FC<HomePageProps> = ({
  entity,
  className,
}) => {
  const { t } = useTranslation();
  const {
    heroImageBreakpointAspectRatios,
    heroTextColClasses,
    heroQuicklinksColClasses,
    quickLinksButtonType,
  } = useLayoutVariables('homePage');

  const homePage = entity.toJS();
  const heroTitle = homePage.title[0].value;
  const heroBody = homePage.body[0]?.processed;
  const heroImage = homePage.field_media_item[0];

  const link = homePage.field_link[0];
  const linkTitle = link?.title;
  const linkUrl = link?.alias || link?.uri;
  const linkSecondary = homePage.field_link_secondary[0];
  const linkSecondaryTitle = linkSecondary?.title;
  const linkSecondaryUrl = linkSecondary?.alias || linkSecondary?.uri;

  const quickLinks = homePage.field_links;
  const classes = classNames('Node', 'NodeHomePage', className);

  return (
    <article itemType="http://schema.org/WebPage" className={classes}>
      <div className="NodeHomePage__hero">
        <div className="NodeHomePage__hero__imagewrapper">
          <div className="NodeHomePage__hero__image">
            <VariableRatiosPicture
              image={heroImage}
              aspectRatios={heroImageBreakpointAspectRatios}
            />
          </div>
        </div>

        <Container>
          <div className="row">
            <div className={classNames(heroTextColClasses, 'NodeHomePage__hero__text-column')}>
              <div className="NodeHomePage__hero__text-wrapper">
                <h1 itemProp="title" className="NodeHomePage__hero__title">{heroTitle}</h1>
                {heroBody && (
                  <WysiwygField
                    className="NodeHomePage__hero__body"
                    property="schema:text"
                    html={heroBody}
                  />
                )}
                {(link || linkSecondary) && (
                  <div className="NodeHomePage__hero__links">
                    {link && (
                      <ButtonWrapper href={linkUrl}>{linkTitle}</ButtonWrapper>
                    )}
                    {linkSecondary && (
                      <ButtonWrapper href={linkSecondaryUrl} type="secondary" inverted>{linkSecondaryTitle}</ButtonWrapper>
                    )}
                  </div>
                )}
              </div>
            </div>

            {quickLinks.length > 0 && (
              <div className={classNames(heroQuicklinksColClasses, 'NodeHomePage__hero__quicklinks-column')}>
                <div className="NodeHomePage__hero__quicklinks-wrapper">
                  <div className="NodeHomePage__hero__quicklinks">
                    <h2>{t('content_types.homepage.quicklinks')}</h2>
                    <div className="NodeHomePage__hero__quicklinks__links">
                      {quickLinks.map((quickLink) => (
                        <ButtonWrapper
                          key={quickLink.title}
                          className="NodeHomePage__hero__quicklinks__link"
                          href={quickLink.alias || quickLink.uri}
                          type={quickLinksButtonType}
                        >
                          {quickLink.title}
                        </ButtonWrapper>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Container>
      </div>

      <ParagraphsRenderer className="NodeHomePage__paragraphs" paragraphs={entity.get('field_paragraphs')} />
    </article>
  );
};

export default NodeHomePage;
